import { equals, isEmpty, isNil, path, pick, values } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { featureCollection, point } from '@turf/helpers';
import { bbox } from '@turf/turf';

import FILTERS from '../enums/RouteDetailFilters';
import { requestTypes, responseTypes } from '../enums/requestTypes';
import MOVE_TYPES from '../enums/moveTypes';
import {
  filterStops,
  findKingParcelPostcode,
  getHighestStopNumber,
  getSelectedParcelsCount,
  isBreakStop,
  isCollectionStop,
  isShopStop,
  makeStopForRequest,
  sortByStopNumber,
} from './stop';
import {
  CRITERIA,
  INCLUDE_EXCLUDE_CRITERIA,
} from '../components/modals/SelectByCriteria/constants';
import { criteriaMapper } from '../features/routeDetail/criteriaModel';
import { getSortedStops } from './moveStops';
import RouteStatus from '../enums/RouteStatus';
import { COLUMN_ACCESSORS as CA } from '../constants/table';
import stopTypes, { originalTypes } from '../enums/stopTypes';
import {
  isDeferredRequest,
  isRemoveRequest,
} from '../features/routeDetail/model';
import { convertParcelStatus } from './parcel';
import { NOT_APPLICABLE, SCAN_TIME_ENTITIES } from '../constants/strings';
import { formatMinutesToMMSS, getTime } from '../utils/date';
import { isAddParcelRequest } from './request';

const {
  EXCEPTIONS,
  PREMIUMS,
  OPPORTUNITY,
  CONSOLIDATED,
  MATCHING_POSTCODES,
  NON_COMPATIBLES,
  NON_LOADED_PARCELS,
  NON_CADS,
} = FILTERS;

export const isDefaultRoute = (routeId, routes) => {
  if (routeId && routes && !isEmpty(routes)) {
    return routes[routeId] && routes[routeId].defaultRoute;
  }
  return false;
};

export const hasException = (stop, exception) =>
  stop && stop.exception === exception;

export const ignoreServiceException = (stop) => stop && stop.ignoreException;

export const isServiceException = (stop) => hasException(stop, 2);

export const hasAnyExceptions = (stop) => {
  if (stop && stop.exception) {
    return !isServiceException(stop) || !ignoreServiceException(stop);
  }
  return false;
};

export const totalRouteExceptions = (stops) =>
  stops.reduce(
    (total, stop) => (hasAnyExceptions(stop) ? total + 1 : total),
    0
  );

export const isPremium = (stop) => stop && stop.premium;

export const isBulk = (stop) => stop && stop.bulk;

export const isPerishable = (stop) => stop && stop.perishable;

export const isVIP = (stop) => stop && stop.vipDelivery;

export const hasOpportunity = (stop) => stop && stop.opportunityTime > 0;

const isConsolidated = (stop) => stop && stop.consolidated;

const hasMatchingPostcodes = (stop, stops = []) => {
  const postcode = findKingParcelPostcode(stop);
  return (
    postcode &&
    stops.some(
      (s) =>
        s.stopNumber !== stop.stopNumber &&
        findKingParcelPostcode(s) === postcode
    )
  );
};

const hasNonCompatibles = (stop) => stop?.nonCompatibles?.length > 0;

const hasNonLoadedParcels = (stop) =>
  stop && stop.totalLoaded < stop.totalParcels;

const hasNonCads = (stop) => stop && stop.totalCadParcels < stop.totalParcels;

export const isRemoveParcelRequest = (request) =>
  request.requestType === requestTypes.REMOVE_PARCEL;

export const isRemoveCollectionRequest = (request) =>
  request.requestType === requestTypes.REMOVE_COLLECTION;

export const getFilterFn = (id) => {
  switch (id) {
    case EXCEPTIONS:
      return hasAnyExceptions;
    case PREMIUMS:
      return isPremium;
    case OPPORTUNITY:
      return hasOpportunity;
    case CONSOLIDATED:
      return isConsolidated;
    case MATCHING_POSTCODES:
      return hasMatchingPostcodes;
    case NON_COMPATIBLES:
      return hasNonCompatibles;
    case NON_LOADED_PARCELS:
      return hasNonLoadedParcels;
    case NON_CADS:
      return hasNonCads;
    default:
      return (x) => x;
  }
};

const satisfyCriteria = (criteria, stop) =>
  criteria.every((item) => {
    if (isBreakStop(stop)) {
      return false;
    }

    if (item.criteria === CRITERIA.ETA && !stop.eta) {
      return false;
    }

    if (item.criteria === CRITERIA.OPPORTUNITY_TIME && !stop.opportunityTime) {
      return false;
    }

    if (item.criteria === CRITERIA.STOP_TIME && !stop.stopTime) {
      return false;
    }

    return item.rule === INCLUDE_EXCLUDE_CRITERIA.INCLUDE
      ? criteriaMapper[item.criteria](item, stop)
      : !criteriaMapper[item.criteria](item, stop);
  });

export const selectedStopsMapper =
  (filters, selectedStops, criteria) => (stop) =>
    selectedStops.includes(stop.stopId) ||
    (criteria.length && satisfyCriteria(criteria, stop))
      ? { ...stop, selected: true }
      : stop;

export const getSatisfyCriteriaStopIds = (criteria, stops) =>
  stops.reduce((acc, stop) => {
    satisfyCriteria(criteria, stop) && acc.push(stop.stopId);
    return acc;
  }, []);

export const getNewSelectedStopNumbers = ({
  stops,
  selectedStops,
  stopsToSelect,
  isFilter,
}) => {
  const allSelectedStops = isFilter
    ? stops.filter((s) => s.selected).map((stop) => stop.stopId)
    : selectedStops;

  const stopsToAdd = [];
  const stopsToRemove = [];

  stopsToSelect.forEach((stopId) => {
    if (allSelectedStops.includes(stopId)) {
      return stopsToRemove.push(stopId);
    }
    stopsToAdd.push(stopId);
  });

  return [
    ...allSelectedStops.filter((id) => !stopsToRemove.includes(id)),
    ...stopsToAdd,
  ];
};

export const getStopsToSelect = ({
  stopId,
  stops,
  previousStopIndex,
  stopIndex,
  shiftKey,
}) => {
  if (isNaN(previousStopIndex) || !shiftKey) {
    return [stopId];
  }

  const [startIndex, endIndex] =
    previousStopIndex < stopIndex
      ? [previousStopIndex + 1, stopIndex + 1]
      : [stopIndex, previousStopIndex];

  return stops.slice(startIndex, endIndex).map((s) => s.stopId);
};
export const mapRouteRequests = (route) => {
  if (!route?.request) {
    return route.stop || [];
  }

  let highestSeqNo = 0;
  const stopsMap = route.stop.reduce((map, stop) => {
    if (stop.sequenceNumber > highestSeqNo) {
      highestSeqNo = stop.sequenceNumber;
    }

    map[stop.stopNumber] = {
      ...stop,
      requests: [],
    };
    return map;
  }, {});

  route.request.forEach((request) => {
    if (request.routeId && request.routeId !== route.routeId) {
      return;
    }

    if (
      request.response &&
      request.response.responseType !== responseTypes.DEFER
    ) {
      return;
    }

    if (stopsMap[request.stopNumber]) {
      stopsMap[request.stopNumber].requests.push(request);
      if (isAddParcelRequest(request) && !stopsMap[request.stopNumber].parcel) {
        stopsMap[request.stopNumber] = {
          ...stopsMap[request.stopNumber],
          ...makeStopForRequest(request),
        };
      }
      return;
    }

    if (
      !stopsMap[request.stopNumber] &&
      !request.routeId &&
      (isRemoveCollectionRequest(request) || isRemoveParcelRequest(request))
    ) {
      return;
    }
    const newStop = makeStopForRequest(request);
    let newStopSequenceNumber;

    if (request.addAfterStop && stopsMap[request.addAfterStop]) {
      newStopSequenceNumber = stopsMap[request.addAfterStop].sequenceNumber + 1;

      Object.values(stopsMap).forEach((stop) => {
        if (stop.sequenceNumber >= newStopSequenceNumber) {
          stop.sequenceNumber += 1;
        }
      });
    } else {
      newStopSequenceNumber = ++highestSeqNo;
    }

    const newStopNumber =
      request.stopNumber ||
      getHighestStopNumber([...route.stop, ...values(stopsMap)]) + 1;

    stopsMap[newStopNumber] = {
      stopNumber: newStopNumber,
      sequenceNumber: newStopSequenceNumber,
      stopId: uuidv4(),
      displayNumber: `${newStopNumber}`,
      requests: [request],
      displayServiceWindow: true,
      serviceStartTime: request.serviceStartTime,
      serviceEndTime: request.serviceEndTime,
      stopAddress: request.address,
      pendingStop: true,
      ...newStop,
    };
  });

  return Object.values(stopsMap);
};

export const getPreparedStops = (
  route,
  { filters, manuallySelectedStops, criteria }
) =>
  (route &&
    mapRouteRequests(route).map(
      selectedStopsMapper(filters, manuallySelectedStops, criteria)
    )) ||
  [];

export const getSelectedPreparedStops = (stops) =>
  stops.filter((stop) => stop.selected);

export const getFilteredStops = ({ filters }, stops, selectedStops) =>
  !filters.length || filters.includes(FILTERS.SHOW_IN_ROUTE)
    ? stops
    : selectedStops;

export const areStopsConsecutive = (selectedStops) => {
  const sequenceNumbers = selectedStops.map((stop) => stop.sequenceNumber);

  return (
    Math.max(...sequenceNumbers) ===
    sequenceNumbers.length - 1 + Math.min(...sequenceNumbers)
  );
};

export const isEdgeStop = (sortedStops, selectedStops, direction) =>
  selectedStops.some(({ stopNumber }) => {
    const index = direction === MOVE_TYPES.UP ? 0 : sortedStops.length - 1;
    return equals(stopNumber, sortedStops[index].stopNumber);
  });

const isCorrectSortToMoveStops = ({ accessor, isAscending }) =>
  accessor === CA.SEQUENCE_NUMBER && isAscending;

export const isMoveStopEnabled = (
  direction,
  selectedStops,
  allStops,
  stopListOrder,
  isRouteStatusInBuild
) => {
  const sortedStops = getSortedStops(allStops);

  return (
    !isRouteStatusInBuild &&
    isCorrectSortToMoveStops(stopListOrder) &&
    areStopsConsecutive(selectedStops) &&
    !isEdgeStop(sortedStops, selectedStops, direction)
  );
};

export const isMoveToEnable = (
  selectedStops,
  stopListOrder,
  isRouteStatusInBuild
) =>
  !isRouteStatusInBuild &&
  !!selectedStops.length &&
  isCorrectSortToMoveStops(stopListOrder);

export const getNextSelectedByFilterStops = (nextFilters, stops) =>
  nextFilters.length
    ? stops.reduce(
        (acc, stop) =>
          stop.stopTypeDescription !== stopTypes.break &&
          nextFilters.every((id) => getFilterFn(id)(stop, stops))
            ? [...acc, stop.stopId]
            : acc,
        []
      )
    : [];

const isSuitableStatus = (route, status) => path(['status'], route) === status;

export const isRouteStatusReady = (route) =>
  isSuitableStatus(route, RouteStatus.READY);

export const isRouteStatusVoid = (route) =>
  isSuitableStatus(route, RouteStatus.VOID);

export const isRouteStatusOpen = (route) =>
  isSuitableStatus(route, RouteStatus.OPEN);

export const isRouteStatusLoading = (route) =>
  isSuitableStatus(route, RouteStatus.LOADING);

export const isRouteStatusInBuild = (route) =>
  isSuitableStatus(route, RouteStatus.IN_BUILD);

export const isRouteStatusDownloaded = (route) =>
  isSuitableStatus(route, RouteStatus.DOWNLOADED);

export const isRouteStatusDeparted = (route) =>
  isSuitableStatus(route, RouteStatus.DEPARTED);

const replaceNullValueToDefault = (value, defaultValue = NOT_APPLICABLE) =>
  !isNil(value) ? value : defaultValue;

export const formatLoadTimeData = ({
  routeStatus,
  totalScanTime = NOT_APPLICABLE,
  routeDownloadTime = NOT_APPLICABLE,
  firstParcelScanTime,
  routeAuthorisedTime,
  targetDepartureTime = NOT_APPLICABLE,
  actualLoadRate = 0,
  targetLoadRate = 0,
}) => ({
  [SCAN_TIME_ENTITIES.TOTAL_SCAN_TIME]: `${formatMinutesToMMSS(
    totalScanTime
  )} Mins`,
  [SCAN_TIME_ENTITIES.TIME_DOWNLOADED]: getTime(routeDownloadTime),
  [SCAN_TIME_ENTITIES.TIME_FIRST_SCAN]:
    routeStatus === RouteStatus.LOADING
      ? getTime(firstParcelScanTime)
      : NOT_APPLICABLE,
  [SCAN_TIME_ENTITIES.TIME_AUTHORISED]:
    routeStatus === RouteStatus.DEPARTED
      ? getTime(routeAuthorisedTime)
      : NOT_APPLICABLE,
  [SCAN_TIME_ENTITIES.TARGET_DEPART_TIME]: !isNil(routeAuthorisedTime)
    ? (!isNil(targetDepartureTime) && getTime(targetDepartureTime)) ||
      NOT_APPLICABLE
    : NOT_APPLICABLE,
  [SCAN_TIME_ENTITIES.LOAD_RATE]: `${replaceNullValueToDefault(
    actualLoadRate
  )} (${replaceNullValueToDefault(targetLoadRate)})`,
});

export const getSelectedStops = (stops, { manuallySelectedStops }) =>
  stops.filter((stop) => manuallySelectedStops.includes(stop.stopId));

const formattedStopData = (stops = [], routeDate) =>
  stops.map((stop) => {
    const parcels = stop.parcel.map((parcel) => ({
      ...pick(
        [
          'parcelCode',
          'parcelNumber',
          'premium',
          'priority',
          'serviceStartTime',
          'serviceEndTime',
          'parcelIndex',
          'networkCode',
          'windowType',
          'additionalMinutes',
          'diaryType',
          'serviceDowngraded',
          'originalNetwork',
          'originalServiceStartTime',
          'originalServiceEndTime',
          'serviceDowngraded',
        ],
        parcel
      ),
      parcelStatus: convertParcelStatus(parcel),
      routeDate: parcel.routeDate || routeDate,
    }));
    const formattedStopAddress =
      stop.stopAddress &&
      pick(
        ['postcode', 'latitude', 'longitude', 'postcodeChanged'],
        stop.stopAddress
      );

    const collection = stop.collection
      ? pick(
          [
            'collectionCode',
            'routeDate',
            'collectionNumber',
            'collectionIndex',
            'collectionStatus',
          ],
          stop.collection
        )
      : {};

    const formattedStop = pick(
      [
        'stopNumber',
        'sequenceNumber',
        'displayNumber',
        'priority',
        'stopType',
        'totalParcels',
        'stopTime',
        'eta',
        'opportunityTime',
        'exception',
        'serviceStartTime',
        'serviceEndTime',
        'propertyType',
        'bulk',
        'consolidated',
        'postcode',
        'latitude',
        'longitude',
        'postcodeChanged',
        'collection',
        'stopId',
        'bestService',
        'singleStop',
        'stopState',
        'ignoreException',
        'reconsolidated',
        'distanceToStop',
      ],
      { ...stop, collection }
    );
    return {
      ...formattedStop,
      parcel: parcels,
      stopAddress: formattedStopAddress,
    };
  });

export const formatRouteDetailData = (
  routeDetail,
  scheduleRequired,
  initialDowngradeAttempts
) => {
  const {
    status,
    checksum,
    routeDate,
    returnToDepot,
    returnToDepotOpportunityTime,
    returnToDepotLegalException,
    totalMiles,
    opportunityTime,
    transferredStop,
  } = routeDetail;

  const requests = routeDetail.request?.map((request) => ({
    ...pick(
      [
        'requestType',
        'requestReason',
        'requestOrigin',
        'labelNumber',
        'parcelCode',
        'collectionCode',
        'pickupUnsuitable',
        'addAfterStop',
        'stopNumber',
        'requestId',
        'collectionReference',
        'cadScan',
        'serviceStartTime',
        'serviceEndTime',
        'address',
        'response',
        'routeId',
        'sourceStopId',
        'sourceParcelStopId',
        'fullStopTransfer',
        'plannedRoute',
        'stopTime',
        'order',
      ],
      request
    ),
    relatedStopParcels: request.relatedStopParcels || [],
    routeDate: request.routeDate || routeDate,
  }));

  return {
    route: {
      routeStatus: status,
      checksum,
      routeDate,
      mileage: totalMiles,
      returnToDepot,
      returnToDepotOpportunityTime,
      returnToDepotLegalException,
      totalRouteOpportunityTime: opportunityTime,
      stop: formattedStopData(routeDetail.stop, routeDate),
      request: requests,
      transferredStop: formattedStopData(transferredStop, routeDate),
      scheduleRequired,
      initialDowngradeAttempts,
      ...pick(
        [
          'startLocationName',
          'startLocationPostcode',
          'startLocationLongitude',
          'startLocationLatitude',
          'endLocationName',
          'endLocationPostcode',
          'endLocationLongitude',
          'endLocationLatitude',
          'finalRouteReadyOptimised',
        ],
        routeDetail
      ),
    },
  };
};

export const getAvailableMergeSplitStops = (leadStop, routeStops) =>
  !leadStop
    ? []
    : sortByStopNumber(
        routeStops.filter((stop) => {
          if (isBreakStop(stop) || isCollectionStop(stop)) {
            return false;
          }

          if (leadStop.stopNumber === stop.stopNumber) {
            return false;
          }

          if (isShopStop(stop)) {
            const currentStopPickupCode = stop.stopAddress?.pickupCode;
            return (
              currentStopPickupCode &&
              currentStopPickupCode === leadStop.stopAddress?.pickupCode
            );
          }

          if (
            !leadStop ||
            isShopStop(leadStop) ||
            leadStop.stopNumber === stop.stopNumber
          ) {
            return false;
          }

          return !stop.swapit;
        })
      );

export const hasDefferResponse = (requests = []) =>
  requests.some((request) => isDeferredRequest(request));

export const isParcelTransferDisabled = (selectedParcels, selectedStop) => {
  const parcelsHasDefferResponse = values(selectedParcels).some(
    ({ requests = [] }) => hasDefferResponse(requests)
  );
  const stopHasDefferResponse = hasDefferResponse(selectedStop?.requests);
  return (
    !getSelectedParcelsCount(selectedParcels) ||
    parcelsHasDefferResponse ||
    stopHasDefferResponse
  );
};

export const isStopTransferDisabled = (selectedStops) =>
  !selectedStops.length ||
  selectedStops.some((stop) => {
    const { stopType, requests } = stop;
    if (stopType === originalTypes.break.stopType) {
      return true;
    }

    return hasDefferResponse(requests);
  });

export const isMergeDisabled = (selectedStops, routeStatusInBuild) =>
  routeStatusInBuild ||
  !selectedStops.length ||
  selectedStops.some(
    ({ stopType, requests }) =>
      requests.length ||
      stopType === originalTypes.collection.stopType ||
      stopType === originalTypes.break.stopType
  );

export const isSplitDisabled = (
  selectedStops,
  routeStatusInBuild,
  selectedCount
) =>
  routeStatusInBuild ||
  !selectedCount ||
  selectedStops.some(({ requests }) => requests.length) ||
  selectedCount === selectedStops[0]?.parcel.length;

export const isBulkDisabled = (
  selectedStops,
  bulkStopThreshold,
  routeStatusInBuild
) =>
  routeStatusInBuild ||
  (bulkStopThreshold &&
    selectedStops.length === 1 &&
    selectedStops[0].parcel.length < bulkStopThreshold);

export const getRouteLockedBy = (routes, routeId) =>
  routes.find(({ routeId: id }) => id === routeId)?.routeLockedBy;

export const getLeadStop = (stops) => stops[0]; // TODO: Lewis should clarify

export const getParcelsFromSelectedStops = (stops = []) =>
  stops.reduce(
    (parcels, stop) => (stop.parcel ? [...parcels, ...stop.parcel] : parcels),
    []
  );

export const getUniqueRoutesFromNextWaves = (parcels) =>
  values(parcels).reduce(
    (routes, parcel) =>
      parcel.nextWave ? [...new Set([...routes, ...parcel.nextWave])] : routes,
    []
  );

export const getUniqueRoutesFromSelectedStops = (stops = []) =>
  getUniqueRoutesFromNextWaves(getParcelsFromSelectedStops(stops));

export const getParcelRequestString = (request) => {
  if (isDeferredRequest(request)) {
    return 'defer';
  } else if (isRemoveRequest(request)) {
    return 'remove';
  } else {
    return 'add';
  }
};

export const getDeliveryRouteStops = (stops) =>
  stops.filter((stop) => !isBreakStop(stop) && !isCollectionStop(stop));

export const sortRouteStops = (route) => {
  if (!route) {
    return;
  }

  return {
    ...route,
    stop: getSortedStops(route.stop),
  };
};

export const getFilteredStopsFromFilterPanel = (route, filters = {}) =>
  (route && filterStops(filters, mapRouteRequests(route))) || [];

export const stopsPinMapper = (filteredStops, { manuallySelectedStops }) =>
  filteredStops.reduce((acc, stop) => {
    const { stopAddress } = stop;
    const isValidStop =
      !isBreakStop(stop) && stopAddress?.longitude && stopAddress?.latitude;
    const stopObj = manuallySelectedStops.includes(stop.stopId)
      ? { ...stop, selected: true }
      : stop;
    return isValidStop
      ? [
          ...acc,
          point(
            [
              stop.stopAddress.longitude / 1000000,
              stop.stopAddress.latitude / 1000000,
            ],
            {
              stop: stopObj,
            }
          ),
        ]
      : acc;
  }, []);

export const getMapFitBounds = (
  stopFeatures = [],
  { coordinates: depotCoordinates }
) => bbox(featureCollection([...stopFeatures, point(depotCoordinates)]));
